/**
 * NewConversationModal Component
 *
 * Modal for starting new conversations with connections.
 * Features user search, connection filtering, first message input,
 * and option to create group conversations.
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search,
  Send,
  Loader2,
  MessageSquare,
  Users,
  Check,
} from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useConnections } from '@/stores/connection';
import { useConversations, useMessagingActions } from '@/stores/messaging';
import { SUCCESS_GREEN } from '@/styles/tokens';
import { CreateGroupModal } from './CreateGroupModal';
import type { UserConnection } from '@/types/connections';

interface NewConversationModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface NewConversationModalInnerProps extends NewConversationModalProps {
  onOpenCreateGroup: () => void;
}

/**
 * Get user initials for avatar fallback
 */
const getInitials = (firstName?: string, lastName?: string, username?: string): string => {
  if (firstName && lastName) {
    return `${firstName.charAt(0)}${lastName.charAt(0)}`.toUpperCase();
  }
  if (firstName) {
    return firstName.charAt(0).toUpperCase();
  }
  if (username) {
    return username.charAt(0).toUpperCase();
  }
  return 'U';
};

/**
 * Get display name for a connection
 */
const getDisplayName = (connection: UserConnection): string => {
  const { user } = connection;
  if (user.firstName && user.lastName) {
    return `${user.firstName} ${user.lastName}`;
  }
  if (user.firstName) {
    return user.firstName;
  }
  return user.username;
};

/**
 * ConnectionCard - User card in the search results
 */
interface ConnectionCardProps {
  connection: UserConnection;
  hasExistingConversation: boolean;
  isSelected: boolean;
  onClick: () => void;
}

const ConnectionCard: React.FC<ConnectionCardProps> = ({
  connection,
  hasExistingConversation,
  isSelected,
  onClick,
}) => {
  const displayName = getDisplayName(connection);
  const initials = getInitials(
    connection.user.firstName,
    connection.user.lastName,
    connection.user.username
  );

  return (
    <button
      onClick={onClick}
      className={`w-full p-4 rounded-lg border transition-all duration-200 text-left ${
        isSelected
          ? 'border-primary bg-primary/5 ring-2 ring-primary/20'
          : 'border-border bg-card hover:border-border hover:bg-muted/50 hover:shadow-sm'
      }`}
    >
      <div className="flex items-center gap-4">
        <div className="relative">
          <Avatar className="h-11 w-11">
            <AvatarImage src={connection.user.avatar} alt={displayName} />
            <AvatarFallback
              className="border border-primary/20 bg-primary/10 text-sm font-semibold text-primary"
            >
              {initials}
            </AvatarFallback>
          </Avatar>
          {/* Online indicator */}
          {connection.user.isOnline && (
            <span
              className="absolute bottom-0 right-0 h-3 w-3 rounded-full border-2 border-card"
              style={{ backgroundColor: SUCCESS_GREEN.DEFAULT }}
              aria-label="Online"
            />
          )}
        </div>

        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate">{displayName}</p>
          <p className="text-xs text-muted-foreground">@{connection.user.username}</p>
        </div>

        <div className="flex items-center gap-2">
          {hasExistingConversation && (
            <Badge
              className="text-xs px-2 py-0.5 bg-primary/10 text-primary border-0"
            >
              <MessageSquare className="h-3 w-3 mr-1" />
              Existing
            </Badge>
          )}
          {isSelected && (
            <div className="h-5 w-5 rounded-full flex items-center justify-center bg-primary">
              <Check className="h-3 w-3 text-primary-foreground" />
            </div>
          )}
        </div>
      </div>
    </button>
  );
};

const NewConversationModalInner: React.FC<NewConversationModalInnerProps> = ({
  open,
  onOpenChange,
  onOpenCreateGroup,
}) => {
  const navigate = useNavigate();
  const connections = useConnections();
  const conversations = useConversations();
  const { startConversation } = useMessagingActions();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedConnection, setSelectedConnection] = useState<UserConnection | null>(null);
  const [message, setMessage] = useState('');
  const [isStarting, setIsStarting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Get user IDs that already have direct conversations
  const existingConversationUserIds = useMemo(() => {
    const userIds = new Set<string>();

    conversations.forEach((conversation) => {
      if (!conversation.isGroup && conversation.participant) {
        userIds.add(conversation.participant.id);
      }
    });

    return userIds;
  }, [conversations]);

  // Filter connections based on search
  const filteredConnections = useMemo(() => {
    if (!searchQuery.trim()) return connections;

    const query = searchQuery.toLowerCase().trim();
    return connections.filter((c) => {
      const name = `${c.user.firstName} ${c.user.lastName}`.toLowerCase();
      const username = c.user.username.toLowerCase();
      return name.includes(query) || username.includes(query);
    });
  }, [connections, searchQuery]);

  // Reset state when modal closes
  useEffect(() => {
    if (!open) {
      setSearchQuery('');
      setSelectedConnection(null);
      setMessage('');
      setError(null);
    }
  }, [open]);

  /**
   * Handle starting the conversation
   */
  const handleStartConversation = useCallback(async () => {
    if (!selectedConnection || !message.trim()) return;

    setIsStarting(true);
    setError(null);

    try {
      const conversation = await startConversation(selectedConnection.user.id, message.trim());

      if (conversation) {
        onOpenChange(false);
        navigate(`/dashboard/messages?conversation=${conversation.id}`);
      } else {
        setError('Failed to start conversation. Please try again.');
      }
    } catch (err) {
      console.error('[NewConversationModal] Failed to start conversation:', err);
      setError(err instanceof Error ? err.message : 'Failed to start conversation');
    } finally {
      setIsStarting(false);
    }
  }, [selectedConnection, message, startConversation, onOpenChange, navigate]);

  /**
   * Handle connection selection
   */
  const handleSelectConnection = (connection: UserConnection) => {
    if (selectedConnection?.connectionId === connection.connectionId) {
      setSelectedConnection(null);
    } else {
      setSelectedConnection(connection);
    }
    setError(null);
  };

  /**
   * Handle opening existing conversation
   */
  const handleOpenExisting = (userId: string) => {
    const existingConversation = conversations.find(
      (c) => !c.isGroup && c.participant?.id === userId
    );
    if (existingConversation) {
      onOpenChange(false);
      navigate(`/dashboard/messages?conversation=${existingConversation.id}`);
    }
  };

  const canStart = selectedConnection && message.trim().length > 0 && !isStarting;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent variant="light" className="max-w-lg max-h-[85vh] flex flex-col p-0 gap-0">
        {/* Header */}
        <DialogHeader className="p-6 pb-4">
          <DialogTitle className="flex items-center gap-3 text-lg font-semibold text-card-foreground">
            <div className="h-9 w-9 rounded-full flex items-center justify-center bg-primary/10">
              <Send className="h-4 w-4 text-primary" />
            </div>
            New Message
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground mt-1 ml-12">
            Start a conversation with one of your connections
          </DialogDescription>
        </DialogHeader>

        {/* Create Group Button */}
        <div className="px-6 pb-4">
          <Button
            variant="outline"
            className="w-full justify-center gap-2"
            onClick={onOpenCreateGroup}
          >
            <Users className="h-4 w-4" />
            Create Group
          </Button>
        </div>

        {/* Divider */}
        <div className="px-6 pb-4">
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <span className="w-full border-t border-border" />
            </div>
            <div className="relative flex justify-center text-xs uppercase">
              <span className="bg-card px-2 text-muted-foreground">Or message a connection</span>
            </div>
          </div>
        </div>

        {/* Search input */}
        <div className="px-6 pb-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search connections..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
            />
          </div>
        </div>

        {/* Connections list */}
        <ScrollArea className="flex-1 min-h-[200px] max-h-[250px]">
          <div className="px-6 pb-4 space-y-2">
            {filteredConnections.length === 0 ? (
              <div className="text-center py-8">
                <div className="h-12 w-12 rounded-full mx-auto mb-3 flex items-center justify-center bg-primary/10">
                  <Users className="h-6 w-6 text-primary" />
                </div>
                <p className="text-sm font-medium mb-1 text-foreground">
                  {searchQuery ? 'No connections found' : 'No connections yet'}
                </p>
                <p className="text-xs text-muted-foreground">
                  {searchQuery
                    ? 'Try a different search term'
                    : 'Connect with users to start messaging'}
                </p>
              </div>
            ) : (
              filteredConnections.map((connection) => {
                const hasExisting = existingConversationUserIds.has(connection.user.id);
                return (
                  <ConnectionCard
                    key={connection.connectionId}
                    connection={connection}
                    hasExistingConversation={hasExisting}
                    isSelected={selectedConnection?.connectionId === connection.connectionId}
                    onClick={() =>
                      hasExisting
                        ? handleOpenExisting(connection.user.id)
                        : handleSelectConnection(connection)
                    }
                  />
                );
              })
            )}
          </div>
        </ScrollArea>

        {/* Message input (shown when connection selected) */}
        {selectedConnection && (
          <div className="px-6 pb-4 border-t border-border pt-4">
            <label className="text-sm font-medium text-foreground mb-2 block">
              Your message to {getDisplayName(selectedConnection)}
            </label>
            <Textarea
              placeholder="Type your first message..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              className="min-h-[100px] resize-none"
              maxLength={5000}
              autoFocus
            />
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-muted-foreground">{message.length}/5000</span>
              {error && <span className="text-xs text-destructive">{error}</span>}
            </div>
          </div>
        )}

        {/* Footer */}
        <DialogFooter>
          <Button
            variant="ghost"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            onClick={handleStartConversation}
            disabled={!canStart}
            className={canStart ? 'bg-primary text-primary-foreground hover:bg-primary/90' : ''}
          >
            {isStarting ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Starting...
              </>
            ) : (
              <>
                <Send className="h-4 w-4 mr-2" />
                Start Conversation
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Wrapper component that manages both modals
 */
const NewConversationModalWrapper: React.FC<NewConversationModalProps> = ({
  open,
  onOpenChange,
}) => {
  const [showCreateGroup, setShowCreateGroup] = useState(false);

  // Handle opening create group from the main modal
  const handleOpenCreateGroup = useCallback(() => {
    onOpenChange(false);
    // Small delay to allow first modal to close
    setTimeout(() => setShowCreateGroup(true), 100);
  }, [onOpenChange]);

  return (
    <>
      <NewConversationModalInner
        open={open}
        onOpenChange={onOpenChange}
        onOpenCreateGroup={handleOpenCreateGroup}
      />
      <CreateGroupModal
        open={showCreateGroup}
        onOpenChange={setShowCreateGroup}
      />
    </>
  );
};

export { NewConversationModalWrapper as NewConversationModal };
export default NewConversationModalWrapper;
